import { FunctionComponent, ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PlatformLoader } from '@flexinet/ui-components';
import { IndexPage } from './pages/IndexPage';
import { PersonasOverviewPage } from './pages/PersonasOverviewPage';
import { AliasesManagementPage } from './pages/AliasesManagementPage';
import { MessagesStatisticsPage } from './pages/MessagesStatisticsPage';
import { TagsManagementPage } from './pages/TagsManagementPage';
import { ContactsOverviewPage } from './pages/ContactsOverviewPage';
import { rbacActions } from '../../config/rbac/actions';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {usePermissionsFailureGuard} from '../../hooks/usePermissionsFailureGuard.ts';

export const AdministrationLayout: FunctionComponent = () => {

    // Interim until FNP-1749: a failed check shows the error page, never a redirect.
    usePermissionsFailureGuard();

    const {
        authorizing,
        allowedActions,
    } = useVerifiedPermissions();

    // These six pages were auth-guarded but never RBAC-guarded, so any signed-in
    // user reached them by typing the URL while the navbar hid the entry behind
    // GetAdministration. The control was hidden, not enforced (FNP-1115).
    //
    // `allowedActions` is empty while VerifiedPermissionsProvider is still
    // authorizing, so checking it before the batch settles redirects every
    // legitimate admin on first render. Hold with a loader instead, mirroring how
    // RequireAuth honours `authResolved` (FNP-1114).
    //
    // ⚠️ GetAdministration is NOT in the deployed AVP schema as of 2026-10-17 —
    // verified against the flexinet-email-avp-infra stores in config/permissions.ts:
    // dev 11xEERursJEmCzobzeG4d, test TbmYTTTPesY2X2KgdT5bza and prod
    // 5rKYGz2SVVZeGKJnDPaiag (namespace FlexiEmail). 18 actions are deployed;
    // this app requests 29. So the effect of this gate today is ADMIN-ONLY: admins
    // pass via the catch-all policy `permit(principal in CognitoGroup "...|admin",
    // action, resource)`, and non-admins are redirected because the action resolves
    // in no other policy. That is the accepted end state for these pages, recorded
    // here so it reads as deliberate rather than as the FNP-892 failure it
    // resembles. Adding the action to the schema is infra work in another repo and
    // is tracked separately off FNP-1115.
    const guard = (page: ReactElement) => {
        if (authorizing) {
            return <PlatformLoader/>;
        }

        return allowedActions.includes(rbacActions.GetAdministration)
            ? page
            : <Navigate to='/' replace/>;
    };

    return (
        <Routes>
            <Route index element={guard(<IndexPage />)} />
            <Route path="personas" element={guard(<PersonasOverviewPage />)} />
            <Route path="aliases" element={guard(<AliasesManagementPage />)} />
            <Route path="messages" element={guard(<MessagesStatisticsPage />)} />
            <Route path="tags" element={guard(<TagsManagementPage />)} />
            <Route path="contacts" element={guard(<ContactsOverviewPage />)} />
            <Route path="*" element={<Navigate to="/administration" replace />} />
        </Routes>
    );
};
